<script setup lang="ts">
const { fetchUserProfile } = useUserData();
const { $kyc } = useNuxtApp();
const { isMobile } = useLayout();

const iframeElement = ref<HTMLIFrameElement | null>(null);

const contentHeight = computed<string>(() => {
  return $kyc.modal?.height || '700px';
});

const isIframeVisible = computed<boolean>(() => {
  return (
    $kyc.integration?.flow === 'default' && $kyc.integration?.type === 'iframe'
  );
});

const isSdkVisible = computed<boolean>(() => {
  return (
    $kyc.integration?.flow === 'default' && $kyc.integration?.type === 'sdk'
  );
});

function onCloseDialog() {
  $kyc.closeKycDialog();

  $kyc.resetEnvironment();

  $kyc.resetOperator();
}

async function startKyc(): Promise<void> {
  await $kyc.startKyc();
}

async function manualRefresh() {
  await $kyc.manualRefresh();
}

function onIframeLoad() {
  $kyc.setDialogLoading(false);
}

async function listenPostMessage(message: MessageEvent) {
  if (message.source !== iframeElement.value?.contentWindow) {
    return;
  }

  await $kyc.listenPostMessage(message);
}

async function start() {
  window.addEventListener('message', listenPostMessage, { passive: true });

  await fetchUserProfile();
}

onMounted(async () => {
  await start();
});

onActivated(async () => {
  await start();
});

onBeforeRouteLeave(() => {
  window.removeEventListener('message', listenPostMessage);
});
</script>

<template>
  <ClientOnly>
    <slot
      name="activator"
      v-bind="{
        startKyc,
        step: $kyc.integration?.step,
        manualRefresh,
        isLoading: $kyc.modal?.isLoading,
      }"
    />

    <BaseModal
      v-model="$kyc.isDialogVisible"
      :custom-class-modal="$style.kycOverlay"
      :custom-class-wrapper="$style.kycDialog"
      hide-close-button
      persistent
      :width="480"
    >
      <div :class="['h-full relative wallet-table m-0', $style.kycInner]">
        <BaseLoading
          class="!bg-auth-bg-primary"
          icon-loader
          :show="$kyc.modal?.isLoading"
        />

        <BaseModalTopBar @close="onCloseDialog" />

        <template v-if="$kyc.integration?.flow === 'manual_approve_pending'">
          <div :class="$style.customFlow">
            <BaseCommonLogo
              always-logo
              class="m-0 p-0 h-8"
              custom-size
            />

            <span :class="$style.title">
              <span>{{
                $t('verification.kyc.title_manual_approve_pending')
              }}</span>
            </span>

            <span :class="$style.description">
              <span>{{
                $t('verification.kyc.desc_manual_approve_pending')
              }}</span>
            </span>

            <template v-if="$kyc.current.id">
              <BaseLoading
                v-if="$kyc.integration?.isLoading"
                :full-screen="false"
                inline
                show
              />

              <button
                v-else
                :class="$style.button"
                @click="manualRefresh"
              >
                <span>{{ $t('verification.check_again') }}</span>
              </button>
            </template>
          </div>
        </template>

        <template v-if="$kyc.integration?.flow === 'mobile'">
          <div :class="$style.customFlow">
            <BaseCommonLogo
              always-logo
              class="m-0 p-0 h-8"
              custom-size
            />

            <span :class="$style.title">
              <span>{{ $t('verification.kyc.title_facial') }}</span>
            </span>

            <span :class="$style.description">
              <span>{{ $t('verification.kyc.desc_facial_mobile') }}</span>
            </span>

            <BaseLoading
              :full-screen="false"
              inline
              show
            />
          </div>
        </template>

        <iframe
          v-show="isIframeVisible"
          ref="iframeElement"
          v-bind="{ ...$kyc.iframe }"
          :class="$style.content"
          :style="{ minHeight: isMobile ? '100%' : contentHeight }"
          @load="onIframeLoad"
        ></iframe>

        <BaseKycOperatorSumsub
          v-show="isSdkVisible && $kyc.operatorName === 'sumsub'"
          :class="$style.content"
          :style="{ minHeight: isMobile ? '100%' : contentHeight }"
          :token="$kyc.sdk?.token || ''"
        />
      </div>
    </BaseModal>
  </ClientOnly>
</template>

<style module lang="postcss">
.kycDialog {
  @apply overflow-hidden;
}

/* Mesmo bug de components/base/modal/Verification.vue: com o z-index
   padrão de Modal.vue (1000), este modal (aberto de dentro do fluxo de
   verificação, já em z-[1100]) ficava atrás dele. Sobe mais um nível pra
   sempre ganhar de qualquer modal anterior na cadeia. */
.kycOverlay {
  @apply z-[1200];
}

.kycInner {
  @apply overflow-y-auto;

  -webkit-overflow-scrolling: touch;
}

.content {
  @apply max-w-full;
}

.customFlow {
  @apply min-h-[100dvh] md:min-h-[690px] max-w-full flex flex-col justify-center items-center text-center;

  .title {
    @apply text-texts w-full text-lg font-bold m-0 mt-8;
  }

  .description {
    @apply text-texts/70 w-full text-sm font-normal opacity-70 mt-4 mb-8 px-10;
  }

  .button {
    @apply mx-auto rounded-md gap-1.5 flex items-center font-bold uppercase py-2.5 focus:outline-none focus:ring-4 appearance-none text-primary;
  }
}
</style>
